/**
 * ScenarioOverlayPicker
 *
 * Reusable multi-select scenario picker used on all three forecast charts.
 *
 * Behaviour:
 * - Always shows "Base Forecast" as the first (non-removable-if-sole) option
 * - All scenarios visible to the user are shown — no status gate
 * - Scenarios grouped by maturity: "Ready for Planning" / "In Progress" / "Inactive"
 * - Visual indicator dot: solid = ready, amber pulsing = in progress, muted = inactive
 * - Lists up to 10 scenarios alphabetically per group in the dropdown
 * - A "More scenarios…" button opens a Dialog with a full searchable list
 * - Scenario names always include their status in brackets: "Cost Cutting Plan (Draft)"
 * - Active selections shown as dismissible colour-coded badges below the trigger
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { ChevronDown, ChevronRight, Check, Search, X, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MultiSelectCombobox } from '../MultiSelectCombobox';
import { Button } from '../ui/button';

import { useTerminology } from '@/hooks/useTerminology';
import { useFormatting } from '../../hooks/useFormatting';
import type { ComprehensiveScenario } from '../../types/scenarios';

// ── Constants ──────────────────────────────────────────────────────────────

export const BASE_ID = '__base__';
export const MAX_DROPDOWN_ITEMS = 10;
export const CHART_LINE_COLOR = 'var(--accent-sky-500)';

export const SCENARIO_COLOURS = [
  'var(--accent-brick-500)',   // brick red   — hue  11°
  'var(--accent-sap-500)',     // sap green   — hue 134°
  'var(--accent-violet-500)', // violet      — hue 315°
  'var(--accent-orange-500)', // burnt orange — hue  40°
  'var(--accent-teal-500)',   // teal        — hue 195°
  'var(--accent-purple-500)', // purple      — hue 355°
  'var(--accent-lagoon-500)', // lagoon/cyan — hue 218°
];

// ── Status helpers ─────────────────────────────────────────────────────────

/** Human-readable status label, Title Cased */
export const formatStatus = (status: ComprehensiveScenario['status']): string => {
  switch (status) {
    case 'draft':            return 'Draft';
    case 'under-review':     return 'Under Review';
    case 'pending-approval': return 'Pending Approval';
    case 'active':           return 'Active';
    case 'committed':        return 'Committed';
    case 'parked':           return 'Parked';
    case 'archived':         return 'Archived';
    case 'rejected':         return 'Rejected';
    default:                 return status;
  }
};

/** Full display label including status: "Cost Cutting Plan (Draft)" */
export const scenarioLabel = (sc: ComprehensiveScenario): string =>
  `${sc.name} (${formatStatus(sc.status)})`;

/**
 * Maturity group — controls visual treatment and section headers.
 * "ready"    → Active — solid colour dot, full opacity
 * "progress" → Draft, Under Review, Pending Approval — amber dot, slightly muted
 * "inactive" → Parked, Rejected, Archived — muted dot, low opacity
 */
type MaturityGroup = 'ready' | 'progress' | 'inactive';

const maturityOf = (sc: ComprehensiveScenario): MaturityGroup => {
  switch (sc.status) {
    case 'active':
      return 'ready';
    case 'draft':
    case 'under-review':
    case 'pending-approval':
      return 'progress';
    default:
      return 'inactive';
  }
};

const MATURITY_LABELS: Record<MaturityGroup, string> = {
  ready:    'Ready for Planning',
  progress: 'In Progress',
  inactive: 'Inactive',
};

// ── Cluster picker types & component ──────────────────────────────────────

/** One candidate series shown in the cluster picker popover */
export interface ClusterCandidate {
  id: string;
  label: string;
  value: number;
  colour: string;
}

interface ClusterPickerPopoverProps {
  candidates: ClusterCandidate[];
  periodLabel: string;
  /** Click coordinates in viewport space (event.clientX / event.clientY). */
  anchorX: number;
  anchorY: number;
  /**
   * The chart container the popover is rendered inside. When provided, the
   * popover is positioned absolutely relative to this container so it scrolls
   * with the chart instead of staying pinned to the viewport.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
  onSelect: (id: string) => void;
  onDismiss: () => void;
}

/**
 * ClusterPickerPopover
 *
 * A small card that appears when the user clicks on a chart area where 2+
 * series are visible. Lists each series with colour swatch, name and value —
 * clicking a row fires the drill-down for that series.
 *
 * When `containerRef` is supplied the popover is anchored to that container
 * (document flow, `position: absolute`) so it scrolls with the page; otherwise
 * it falls back to viewport-pinned `position: fixed`.
 */
export const ClusterPickerPopover: React.FC<ClusterPickerPopoverProps> = ({
  candidates,
  periodLabel,
  anchorX,
  anchorY,
  containerRef,
  onSelect,
  onDismiss,
}) => {
  const { formatCurrency } = useFormatting();
  const ref = useRef<HTMLDivElement>(null);

  const popoverWidth = 260;
  const popoverEstimatedHeight = 56 + candidates.length * 40;

  // Flip above the click point when it would overflow the bottom of the
  // viewport — computed from viewport coordinates in both positioning modes.
  const flipUp = anchorY + popoverEstimatedHeight > window.innerHeight;

  const container = containerRef?.current;
  let style: React.CSSProperties;
  if (container) {
    // Anchor relative to the chart container so the popover travels with the
    // chart as the page scrolls.
    const rect = container.getBoundingClientRect();
    const relX = anchorX - rect.left + container.scrollLeft;
    const relY = anchorY - rect.top + container.scrollTop;
    const left = Math.max(0, Math.min(relX, rect.width - popoverWidth - 16));
    const top = flipUp ? relY - popoverEstimatedHeight - 8 : relY + 8;
    style = { position: 'absolute', left, top, width: popoverWidth, zIndex: 40 };
  } else {
    // Fallback: keep the popover inside the viewport (legacy behaviour).
    const left = Math.min(anchorX, window.innerWidth - popoverWidth - 16);
    const top = flipUp ? anchorY - popoverEstimatedHeight - 8 : anchorY + 8;
    style = { position: 'fixed', left, top, width: popoverWidth, zIndex: 40 };
  }

  return (
    <div
      ref={ref}
      style={style}
      className="bg-background border rounded-lg shadow-xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {periodLabel} — select series
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors ml-2"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Candidate rows */}
      <div className="p-1">
        {candidates.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => { onSelect(c.id); }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <span
              className="h-3 w-3 rounded-full flex-shrink-0 border-2"
              style={{ backgroundColor: c.colour, borderColor: c.colour }}
            />
            <span className="flex-1 text-left truncate">{c.label}</span>
            <span className="font-semibold text-xs tabular-nums text-muted-foreground">
              {formatCurrency(c.value)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Types ──────────────────────────────────────────────────────────────────

interface ScenarioOverlayPickerProps {
  scenarios: ComprehensiveScenario[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export const ScenarioOverlayPicker: React.FC<ScenarioOverlayPickerProps> = ({
  scenarios,
  selectedIds,
  onToggle,
}) => {
  const { getForecastTerm, getScenarioTerm } = useTerminology();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownExpanded, setDropdownExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  type ScenarioStatus = ComprehensiveScenario['status'];
  const ALL_STATUSES: ScenarioStatus[] = ['active', 'draft', 'under-review', 'pending-approval', 'committed', 'parked', 'archived'];
  const STATUS_LABELS: Record<ScenarioStatus, string> = {
    'active': 'Active',
    'draft': 'Draft',
    'under-review': 'Under Review',
    'pending-approval': 'Pending Approval',
    'committed': 'Committed',
    'parked': 'Parked',
    'archived': 'Archived',
    'rejected': 'Rejected',
  };

  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'recent'>('name');
  const [creatorFilter, setCreatorFilter] = useState<string>('all');

  const [collapsedGroups, setCollapsedGroups] = useState<Set<ScenarioStatus>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Record<string, number>>(
    Object.fromEntries(ALL_STATUSES.map(s => [s, 10]))
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseLabel = `Base ${getForecastTerm(true)}`;

  // All scenarios sorted by chosen sort
  const sortedScenarios = [...scenarios].sort((a, b) =>
    sortBy === 'name'
      ? a.name.localeCompare(b.name)
      : new Date(b.modifiedAt ?? b.createdAt ?? 0).getTime() - new Date(a.modifiedAt ?? a.createdAt ?? 0).getTime()
  );

  // Group by maturity
  const groupedScenarios: Record<MaturityGroup, ComprehensiveScenario[]> = {
    ready:    sortedScenarios.filter(sc => maturityOf(sc) === 'ready'),
    progress: sortedScenarios.filter(sc => maturityOf(sc) === 'progress'),
    inactive: sortedScenarios.filter(sc => maturityOf(sc) === 'inactive'),
  };

  // Total for "more" threshold — only ready + progress shown in dropdown
  // inactive scenarios are only accessible via the modal (they're not planning tools)
  const dropdownCandidates = [...groupedScenarios.ready, ...groupedScenarios.progress];
  const dropdownScenarios  = dropdownCandidates.slice(0, MAX_DROPDOWN_ITEMS);
  const hasMore = dropdownCandidates.length > MAX_DROPDOWN_ITEMS || groupedScenarios.inactive.length > 0;
  const moreCount = sortedScenarios.length - dropdownScenarios.length;

  // Unique creators derived from the scenario list
  const uniqueCreators = useMemo(() => {
    const map = new Map<string, string>();
    for (const sc of scenarios) {
      if (sc.createdBy && sc.createdByName) {
        map.set(sc.createdBy, sc.createdByName);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [scenarios]);

  // Filtered list for modal search — all scenarios, with optional status + creator filter
  const filteredScenarios = sortedScenarios.filter(sc => {
    const matchesSearch = `${sc.name} ${sc.status}`.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = statusFilters.length === 0 || statusFilters.includes(sc.status);
    const matchesCreator = creatorFilter === 'all' || sc.createdBy === creatorFilter;
    return matchesSearch && matchesFilter && matchesCreator;
  });

  const filteredByStatus = (status: ScenarioStatus) =>
    filteredScenarios.filter(sc => sc.status === status);

  const colourFor = (id: string): string => {
    const idx = selectedIds.indexOf(id);
    return SCENARIO_COLOURS[idx >= 0 ? idx % SCENARIO_COLOURS.length : 0];
  };

  const labelFor = (id: string): string => {
    if (id === BASE_ID) return baseLabel;
    const sc = scenarios.find(s => s.id === id);
    return sc ? scenarioLabel(sc) : id;
  };

  const overlayIds = selectedIds.filter(id => id !== BASE_ID);
  const triggerLabel = (() => {
    if (overlayIds.length === 0) return 'Add scenario overlay…';
    if (overlayIds.length === 1) return labelFor(overlayIds[0]);
    return `${overlayIds.length} overlays active`;
  })();

  // ── Shared row renderer ──────────────────────────────────────────────────

  const OptionRow = ({
    id,
    label,
    sc,
    compact = false,
  }: { id: string; label: string; sc?: ComprehensiveScenario; compact?: boolean }) => {
    const selIdx = selectedIds.indexOf(id);
    const isSelected = selIdx !== -1;
    const colour = isSelected ? SCENARIO_COLOURS[selIdx % SCENARIO_COLOURS.length] : undefined;
    const group  = sc ? maturityOf(sc) : 'ready';
    const muted  = group === 'inactive';

    return (
      <button
        type="button"
        onClick={() => onToggle(id)}
        className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${compact ? 'py-2' : ''} ${muted ? 'opacity-60' : ''}`}
      >
        {/* Colour swatch when selected, grey dot when not */}
        {isSelected ? (
          <span
            className="mr-2 h-3 w-3 rounded-full flex-shrink-0 border"
            style={{ backgroundColor: colour, borderColor: colour }}
          />
        ) : (
          <span className="mr-2 h-2.5 w-2.5 rounded-full flex-shrink-0 bg-muted-foreground/30" />
        )}
        <span className={`flex-1 text-left ${isSelected ? 'font-semibold' : ''}`}>{label}</span>
        {/* Issues alert icon — shown when scenario has unresolved issues */}
        {sc && sc.issues && sc.issues.filter((i: { resolution?: string }) => !i.resolution).length > 0 && (
          <span
            title={`${sc.issues.filter((i: { resolution?: string }) => !i.resolution).length} unresolved issue${sc.issues.filter((i: { resolution?: string }) => !i.resolution).length !== 1 ? 's' : ''} — open scenario to review`}
            className="mr-1 flex-shrink-0"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          </span>
        )}
        {isSelected && (
          <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
            <Check className="h-4 w-4" />
          </span>
        )}
      </button>
    );
  };

  // ── Group section for dropdown ───────────────────────────────────────────

  const DropdownGroup = ({
    group,
    items,
  }: { group: MaturityGroup; items: ComprehensiveScenario[] }) => {
    if (items.length === 0) return null;
    return (
      <>
        <div className="mx-2 my-1 border-t" />
        <div className="px-2 py-1 text-sm font-semibold text-foreground">
          {MATURITY_LABELS[group]}
        </div>
        {items.map(sc => (
          <OptionRow key={sc.id} id={sc.id} label={scenarioLabel(sc)} sc={sc} />
        ))}
      </>
    );
  };

  // ── Group section for modal ──────────────────────────────────────────────

  const ModalGroup = ({
    status,
    items,
  }: { status: ScenarioStatus; items: ComprehensiveScenario[] }) => {
    if (items.length === 0) return null;
    const isCollapsed = collapsedGroups.has(status);
    const visibleCount = expandedGroups[status] ?? 10;
    const visibleItems = items.slice(0, visibleCount);
    const hasMoreItems = items.length > visibleCount;
    const remaining = items.length - visibleCount;
    const toggleCollapse = () => {
      setCollapsedGroups(prev => {
        const next = new Set(prev);
        if (next.has(status)) next.delete(status);
        else next.add(status);
        return next;
      });
    };
    return (
      <div>
        <div className="sticky top-0 bg-background z-10">
          <button
            type="button"
            onClick={toggleCollapse}
            className="w-full flex items-center justify-between py-2 hover:text-foreground transition-colors group"
          >
            <span className="text-sm font-semibold text-foreground">{STATUS_LABELS[status]}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">
                {items.length} scenario{items.length !== 1 ? 's' : ''}
              </span>
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
            </div>
          </button>
          <div className="border-t mb-1" />
        </div>
        {!isCollapsed && (
          <div>
            {visibleItems.map(sc => (
              <OptionRow key={sc.id} id={sc.id} label={scenarioLabel(sc)} sc={sc} compact />
            ))}
            {hasMoreItems && (
              <button
                type="button"
                onClick={() => setExpandedGroups(prev => ({ ...prev, [status]: (prev[status] ?? 10) + 10 }))}
                className="w-full text-xs text-muted-foreground hover:text-foreground py-2 text-center hover:bg-accent rounded transition-colors"
              >
                Show {Math.min(remaining, 10)} more of {remaining} remaining…
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* ── Trigger button + inline dropdown ── */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => { setDropdownOpen(prev => { if (prev) setDropdownExpanded(false); return !prev; }); }}
          className="flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-transparent text-sm transition-colors hover:bg-accent hover:text-accent-foreground min-w-[200px] justify-between"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 top-10 z-50 min-w-[280px] rounded-md border bg-popover text-popover-foreground shadow-md">
            <div className="p-1">
              <div className="px-2 py-1.5 text-sm font-semibold text-foreground">
                Overlay {getScenarioTerm(true)}
              </div>

              {/* Scenarios grouped by status */}
              {(() => {
                const allStatuses: ScenarioStatus[] = ['active', 'draft', 'under-review', 'pending-approval', 'committed', 'parked', 'archived'];
                const shownItems: ComprehensiveScenario[] = [];
                const groups = allStatuses
                  .map(status => ({
                    status,
                    items: filteredScenarios.filter(sc => sc.status === status),
                  }))
                  .filter(g => g.items.length > 0);

                const visibleGroups = dropdownExpanded ? groups : (() => {
                  const result: typeof groups = [];
                  let count = 0;
                  for (const g of groups) {
                    if (count >= MAX_DROPDOWN_ITEMS) break;
                    const slice = g.items.slice(0, MAX_DROPDOWN_ITEMS - count);
                    result.push({ status: g.status, items: slice });
                    count += slice.length;
                  }
                  return result;
                })();

                return visibleGroups.map(g => (
                  <React.Fragment key={g.status}>
                    <div className="mx-2 my-1 border-t" />
                    <div className="px-2 py-1 text-sm font-semibold text-foreground">
                      {STATUS_LABELS[g.status]}
                    </div>
                    {g.items.map(sc => (
                      <OptionRow key={sc.id} id={sc.id} label={scenarioLabel(sc)} sc={sc} />
                    ))}
                  </React.Fragment>
                ));
              })()}

              {/* View all — opens slide-out drawer */}
              {hasMore && (
                <>
                  <div className="mx-2 my-1 border-t" />
                  <button
                    type="button"
                    onClick={() => { setDropdownOpen(false); setModalOpen(true); }}
                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    View all {getScenarioTerm(false)}
                    {moreCount > 0 && (
                      <span className="mx-1 text-xs opacity-60">+{moreCount}</span>
                    )}
                    <ChevronRight className="ml-auto h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>



      {/* ── "More scenarios" slide-out drawer ── */}
      <Sheet open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) { setSearch(''); setStatusFilters([]); setSortBy('name'); setCreatorFilter('all'); setExpandedGroups(Object.fromEntries(ALL_STATUSES.map(s => [s, 10]))); setCollapsedGroups(new Set()); } }}>
        <SheetContent side="right" className="!w-[50vw] !max-w-none !p-0 !gap-0 overflow-hidden">

          {/* Fixed top section */}
          <div className="flex-shrink-0">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b">
              <SheetTitle className="text-lg font-semibold pr-6">All {getScenarioTerm(true)}</SheetTitle>
            </div>

            {/* Search + Status filter + Created By */}
            <div className="flex items-center gap-2 px-6 pt-4 pb-4 border-b">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-[10px] h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search by name or status…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
              <div className="w-[180px] shrink-0">
                <MultiSelectCombobox
                  options={ALL_STATUSES.filter(s => sortedScenarios.some(sc => sc.status === s)).map(s => STATUS_LABELS[s])}
                  selected={statusFilters.map(s => STATUS_LABELS[s as ScenarioStatus] ?? s)}
                  onChange={labels => setStatusFilters(
                    labels.map(label => (Object.entries(STATUS_LABELS).find(([, v]) => v === label)?.[0] ?? label) as ScenarioStatus)
                  )}
                  placeholder="All Statuses"
                />
              </div>
              {uniqueCreators.length > 1 && (
                <Select value={creatorFilter} onValueChange={setCreatorFilter}>
                  <SelectTrigger className="h-9 text-xs w-[140px] shrink-0">
                    <SelectValue placeholder="Everyone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone</SelectItem>
                    {uniqueCreators.map(([id, name]) => (
                      <SelectItem key={id} value={id}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Sort + Date range row */}
            <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b flex-wrap">
              {/* Sort */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Sort:</span>
                {(['name', 'recent'] as const).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSortBy(s)}
                    className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                      sortBy === s
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    {s === 'name' ? 'A–Z' : 'Recently Modified'}
                  </button>
                ))}
              </div>


            </div>
          </div>

          {/* Scrollable list — takes remaining height */}
          <div className="overflow-y-auto px-6 pb-4" style={{ height: 'calc(100vh - 270px)' }}>
            {filteredScenarios.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No {getScenarioTerm(false)} match "{search}"
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {ALL_STATUSES.map(s => (
                  <ModalGroup key={s} status={s} items={filteredByStatus(s)} />
                ))}
              </div>
            )}
          </div>

          {/* Fixed footer */}
          <div className="flex-shrink-0 flex items-center text-xs text-muted-foreground px-6 py-4 border-t gap-4">
              <span>
                {scenarios.length} {getScenarioTerm(false)} total
              </span>
              <span>{selectedIds.filter(id => id !== BASE_ID).length} of {scenarios.length} selected</span>
              <button
                type="button"
                onClick={() => {
                  for (const id of selectedIds.filter(id => id !== BASE_ID)) {
                    onToggle(id);
                  }
                }}
                disabled={selectedIds.filter(id => id !== BASE_ID).length === 0}
                className="text-xs text-destructive hover:text-destructive/80 underline underline-offset-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:no-underline"
              >
                Deselect all
              </button>
          </div>

        </SheetContent>
      </Sheet>
    </>
  );
};
